import { CheckCircleTwoTone, DownOutlined } from '@ant-design5/icons';
import { type ItemType } from 'antd5/es/menu/interface';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { DataTableContext } from 'components/DataTable';
import { StyledTitle } from 'components/DataTable/Views/Views.style';
import { Button, Dropdown, Tooltip } from 'components/ui';
import {
  type AtlasGqlTableView,
  useConfiguredMakeModelCombinationsQuery,
} from 'types/atlas-graphql';
import { uuidRegex as uuidRegexString } from 'utils/constants';

import { getRelevantBladeCards } from './getRelevantBladeCards';
import { useBladeCardViews } from './useBladeCardViews';

const uuidRegex = new RegExp(uuidRegexString);

type BladeCardsProps = {
  tableId?: string;
  applyViewToTable: (tableViewToApply?: AtlasGqlTableView) => void;
  onSelect?: () => void;
};

export const BladeCards: React.FC<BladeCardsProps> = ({ tableId, applyViewToTable, onSelect }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { tableView } = useContext(DataTableContext);
  const location = useLocation();

  const { views, loading } = useBladeCardViews();

  const isSiteDetailsPage = tableId === 'site-damage-list-table2';
  const isPotentiallyTurbineDetailsPage = tableId === 'assets-damage-list-table2';
  const assetId = location.pathname.split('/')[2];

  const hasScopedAssetId = uuidRegex.test(assetId);

  const { data: turbineMakeModelData } = useConfiguredMakeModelCombinationsQuery({
    variables: {
      input: {
        assetTypeName: 'Turbine',
        ...(isSiteDetailsPage && hasScopedAssetId ? { siteIds: [assetId] } : {}),
        ...(isPotentiallyTurbineDetailsPage && hasScopedAssetId ? { assetIds: [assetId] } : {}),
      },
    },
  });
  const { data: bladeMakeModelData } = useConfiguredMakeModelCombinationsQuery({
    variables: {
      input: {
        assetTypeName: 'Blade',
        ...(isSiteDetailsPage && hasScopedAssetId ? { siteIds: [assetId] } : {}),
        ...(isPotentiallyTurbineDetailsPage && hasScopedAssetId
          ? { ancestorAssetIds: [assetId] }
          : {}),
      },
    },
  });

  const relevantViews = getRelevantBladeCards(
    views,
    turbineMakeModelData?.configuredMakeModelCombinations.existingValues ?? [],
    bladeMakeModelData?.configuredMakeModelCombinations.existingValues ?? []
  );

  const items: ItemType[] = relevantViews.map(view => ({
    key: view.id,
    label: (
      <StyledTitle>
        {view.id === tableView?.id && <CheckCircleTwoTone twoToneColor={theme.green} />}
        <span className="blade-card-name">{view.name}</span>
      </StyledTitle>
    ),
    onClick: () => {
      if (tableId) {
        applyViewToTable({ ...view, tableName: tableId });
      }
      if (onSelect) {
        onSelect();
      }
    },
  }));

  const tooltipMessage = !!items.length
    ? t(
        'damage.defectPatternsTooltip',
        'Data indicates the Damages within these regions may see higher risks of rapid Damage Propagation over time.'
      )
    : t(
        'damage.defectPatternsEmptyTooltip',
        'There are no known Defect Patterns that match your Damage data. Filling in Blade Make/Model on your Assets may make some available.'
      );

  return (
    <Dropdown
      menu={{ items, style: { maxHeight: 350, overflowY: 'scroll' } }}
      trigger={['click']}
      placement="bottomLeft"
    >
      <Tooltip title={tooltipMessage}>
        <Button data-testid="blade-cards-dropdown" disabled={!items.length} loading={loading}>
          {t('damage.defectPatterns', 'Defect Patterns')} <DownOutlined />
        </Button>
      </Tooltip>
    </Dropdown>
  );
};
